import React from 'react'

import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { Paragraph, SectionHeading } from './_shared/Prose'
import { TABLE_ACCENT_DARK_RED } from '../_helpers/brand'

/**
 * Seção 11 - Plano de Ação: Definição e Aplicabilidade. Texto literal do modelo aprovado
 * por Julia Buzzo ("Modelo PGR 2026 - Banco Bradesco_Varejo"), item 11 (pág. 38-39).
 *
 * Este conteúdo (texto + diagrama "Macroprocessos") já existia no código, mas vivia
 * incorretamente no arquivo/slot "13" (`13.PlanoDeAcaoDefinicaoMonitoramentoSection.js`,
 * removido nesta revisão), já rotulado internamente com `numero={11}` — porque os slots de
 * arquivo "11" e "12" estavam ocupados por conteúdo duplicado da Seção 9 (ver nota em
 * `10.ClassificacaoRiscosErgonomicoPsicossocialSection.js`). Agora que a Seção 10 real foi
 * implementada e os slots "10"/"11"/"12" foram corrigidos, este conteúdo passa a viver no
 * arquivo com o número que sempre teve no documento: "11". Ver `PdfDocument.js` para o
 * mapa completo antes→depois.
 *
 * Julia reportou adicionalmente (com screenshot do modelo) que o diagrama "Macroprocessos"
 * aqui não batia com o modelo real: "no ponto 11 do documento o Macroprocesso não está como
 * no documento modelo, precisa ser igual". O diagrama anterior (`FluxogramaMacroprocessos`,
 * em `13...js`) era um fluxo horizontal simplificado
 * (Macroprocessos→GRO→Documentos→PGR, com duas colunas paralelas abaixo) que não
 * corresponde ao diagrama real. Redesenhado abaixo para reproduzir a estrutura exata do
 * modelo (confirmado por inspeção visual do PDF-modelo, pág. 39): uma caixa "Macroprocessos"
 * no topo; uma primeira linha com a caixa "GRO" à esquerda e, à direita, "Diagnóstico dos
 * Perigos e Riscos" + "Análise dos Riscos" → "Controle dos Riscos"; um rótulo "Documentos";
 * e uma segunda linha com a caixa "PGR" à esquerda e, à direita, "Inventário dos Riscos
 * Ocupacionais" e, alinhado sob "Controle dos Riscos" (com uma seta vertical conectando as
 * duas linhas), "Plano de Ação do PGR". Cores/tom de vermelho (`TABLE_ACCENT_DARK_RED`,
 * #C00000) confirmados por amostragem de pixel — as caixas preenchidas e o texto/linhas do
 * diagrama usam o mesmo tom no modelo real. Reproduzido com `View`/`Text`/bordas (sem SVG),
 * consistente com o resto do documento, que não usa nenhum componente de desenho vetorial.
 *
 * "INVENTÁRIO DOS RICOS OCUPACIONAIS": grafia exatamente como aparece no modelo real (typo
 * de "RISCOS" no diagrama original) — mantido verbatim porque Julia pediu reprodução exata
 * ("precisa seguir igual (===)") deste diagrama especificamente.
 */

const DiagramBox = ({ children, style }) => (
  <View
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: TABLE_ACCENT_DARK_RED,
      padding: '4px 10px',
      ...style
    }}
  >
    <Text bold size="small" color="#FFFFFF">
      {children}
    </Text>
  </View>
)

const DiagramLabel = ({ children, align = 'center', bold }) => (
  <Text bold={bold} size="xsmall" color={TABLE_ACCENT_DARK_RED} align={align}>
    {children}
  </Text>
)

const VerticalArrow = () => (
  <Text bold size="small" color={TABLE_ACCENT_DARK_RED} align="center">
    ↓
  </Text>
)

/**
 * Diagrama "Macroprocessos" — fluxo GRO/Documentos/PGR, reproduzindo a estrutura exata do
 * modelo (ver nota do arquivo). Duas linhas horizontais (GRO e PGR), cada uma com uma caixa
 * vermelha à esquerda e o texto do fluxo à direita; a coluna direita da segunda linha
 * ("Plano de Ação do PGR") recebe uma seta vertical vinda da primeira linha ("Controle dos
 * Riscos"), reproduzindo a linha de conexão do modelo.
 */
const FluxogramaMacroprocessos = () => (
  <View style={{ padding: '8px 0' }}>
    <DiagramBox style={{ width: 150 }}>Macroprocessos</DiagramBox>
    <Separator weight={8} />

    <View
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderTop: `2px solid ${TABLE_ACCENT_DARK_RED}`,
        borderBottom: `2px solid ${TABLE_ACCENT_DARK_RED}`
      }}
    >
      <DiagramBox style={{ width: 90, flex: '0 0 auto' }}>GRO</DiagramBox>
      <View
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          gap: 6
        }}
      >
        <DiagramLabel>DIAGNÓSTICO DOS PERIGOS E RISCOS</DiagramLabel>
        <DiagramLabel bold>+</DiagramLabel>
        <DiagramLabel>ANÁLISE DOS RISCOS</DiagramLabel>
        <DiagramLabel bold>→</DiagramLabel>
        <DiagramLabel align="right">CONTROLE DOS RISCOS</DiagramLabel>
      </View>
    </View>

    <View style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '8%' }}>
      <VerticalArrow />
    </View>

    <View style={{ paddingLeft: 90 }}>
      <DiagramBox style={{ width: 130, alignSelf: 'flex-start' }}>Documentos</DiagramBox>
    </View>

    <View
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderTop: `2px solid ${TABLE_ACCENT_DARK_RED}`,
        borderBottom: `2px solid ${TABLE_ACCENT_DARK_RED}`
      }}
    >
      <DiagramBox style={{ width: 90, flex: '0 0 auto' }}>PGR</DiagramBox>
      <View
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          gap: 6
        }}
      >
        <DiagramLabel>INVENTÁRIO DOS RICOS OCUPACIONAIS</DiagramLabel>
        <DiagramLabel align="right">PLANO DE AÇÃO DO PGR</DiagramLabel>
      </View>
    </View>
  </View>
)

const PlanoDeAcaoDefinicaoAplicabilidadeSection = () => (
  <View id="11">
    <Separator weight={15} />
    <SectionHeading numero={11}>PLANO DE AÇÃO: DEFINIÇÃO E APLICABILIDADE</SectionHeading>

    <Paragraph>
      Com base nas informações levantadas durante a elaboração do PGR, será estruturado um Plano de Ação
      estratégico, com definição clara de atividades, metas e prioridades. O foco é promover a eliminação, redução
      ou controle eficaz dos riscos ocupacionais identificados, assegurando intervenções alinhadas às necessidades
      reais da organização.
    </Paragraph>
    <Paragraph>
      Na elaboração do Plano de Ação, será considerada a identificação dos perigos e riscos ocupacionais descritos
      no Inventário de Riscos, com o objetivo de direcionar de forma assertiva as ações necessárias para minimizar e
      controlar os riscos que demandem atenção especial, conforme o grau de risco identificado.
    </Paragraph>
    <Paragraph>
      Cada ação será acompanhada da definição de medidas de prevenção e proteção, bem como orientações, materiais
      informativos e outras iniciativas que se façam necessárias para garantir a efetividade das medidas adotadas e
      a segurança dos funcionários da Organização Bradesco.
    </Paragraph>
    <Paragraph>
      As ações contempladas no Plano de Ação serão devidamente monitoradas e acompanhadas durante a gestão do PGR,
      com o registro documental da evolução das medidas preventivas e melhoria dos ambientes de trabalho.
    </Paragraph>
    <Paragraph>O fluxo em seguida representa a estrutura ilustrativa do funcionamento do Plano de Ação do PGR.</Paragraph>

    <FluxogramaMacroprocessos />

    <Paragraph>
      O Plano de Ação será parte deste PGR, sendo apresentada sua estrutura no item: 17.1. Anexos, subitem: 17.1.4.
      Plano de Ação do PGR, com todas as suas demandas e ações necessárias.
    </Paragraph>

    <Separator weight={15} />
  </View>
)

export default PlanoDeAcaoDefinicaoAplicabilidadeSection
